import {
    AlertTriangle,
    ArrowRight,
    CalendarClock,
    ChevronLeft,
    Clock3,
    Info,
    LoaderCircle,
    Lock,
    ReceiptText,
    Settings2,
    Sparkles,
    ToggleLeft,
    ToggleRight,
    Unlock,
} from 'lucide-react'
import React, { useContext, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import styles from './CycleConfigSetupScreen.module.css'

const parseActiveStore = (rawStore) => {
    try {
        return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
    } catch {
        return null
    }
}

/* ─── LWP live-preview inline component ─── */
const LwpPreview = ({ method }) => {
    const sampleSalary = 12000
    const calendarDays = 30
    const workingDays = 26
    const perDay =
        method === 'PER_WORKING_DAY'
            ? (sampleSalary / workingDays).toFixed(2)
            : (sampleSalary / calendarDays).toFixed(2)

    const divisor = method === 'PER_WORKING_DAY' ? `${workingDays} working days` : `${calendarDays} calendar days`

    return (
        <div className={styles.lwpPreviewCard}>
            <span className={styles.lwpPreviewTitle}>
                <Info size={12} /> Example
            </span>
            <p className={styles.lwpPreviewText}>
                ₹{sampleSalary.toLocaleString('en-IN')} salary, 1 LWP day →{' '}
                <strong>
                    ₹{sampleSalary.toLocaleString('en-IN')} ÷ {divisor} = ₹{perDay}/day deducted
                </strong>
            </p>
        </div>
    )
}

/* ─── Main Screen ─── */
const CycleConfigSetupScreen = () => {
    const navigate = useNavigate()
    const authCtx = useContext(AuthContext)
    const asCtx = useContext(ActiveStoreContext)
    const activeStore = parseActiveStore(asCtx?.activeStore)

    const [formData, setFormData] = useState({
        cycleType: 'MONTHLY',
        cycleStartDay: 1,
        payDay: 5,
        payDayOffset: 'NEXT_MONTH',
        lwpCalculationMethod: 'PER_WORKING_DAY',
        halfDayCountsAs: 0.5,
        gracePeriodMinutes: 15,
        lateDeductionEnabled: false,
        lateDeductionAfterCount: 3,
        lateDeductionType: 'PER_LATE',
        lateDeductionAmount: '',
        autoLockOnPayDay: false,
    })
    const [errors, setErrors] = useState({})
    const [isSaving, setIsSaving] = useState(false)

    const setField = (key, value) => {
        setFormData((prev) => ({ ...prev, [key]: value }))
        setErrors((prev) => {
            if (!prev[key]) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const createCycleConfig = async () => {
        if (!authCtx?.token || !activeStore?.storeIdentifier) return

        /* Client-side validation */
        const newErrors = {}
        if (formData.cycleStartDay < 1 || formData.cycleStartDay > 28) {
            newErrors.cycleStartDay = 'Must be between 1 and 28.'
        }
        if (formData.payDay < 1 || formData.payDay > 28) {
            newErrors.payDay = 'Must be between 1 and 28.'
        }
        if (formData.gracePeriodMinutes < 0 || formData.gracePeriodMinutes > 60) {
            newErrors.gracePeriodMinutes = 'Must be between 0 and 60.'
        }
        if (
            formData.lateDeductionEnabled &&
            formData.lateDeductionType === 'PER_LATE' &&
            (!formData.lateDeductionAmount || parseFloat(formData.lateDeductionAmount) <= 0)
        ) {
            newErrors.lateDeductionAmount = 'Enter a deduction amount per late.'
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors)
            return
        }
        setErrors({})

        try {
            setIsSaving(true)

            const response = await fetch(BASE_URL_API + 'createStoreSalaryCycleConfig', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    storeIdentifier: activeStore.storeIdentifier,
                    cycleType: formData.cycleType,
                    cycleStartDay: formData.cycleStartDay,
                    payDay: formData.payDay,
                    payDayOffset: formData.payDayOffset,
                    lwpCalculationMethod: formData.lwpCalculationMethod,
                    halfDayCountsAs: formData.halfDayCountsAs,
                    gracePeriodMinutes: formData.gracePeriodMinutes,
                    lateDeductionEnabled: formData.lateDeductionEnabled ? 1 : 0,
                    lateDeductionAfterCount: formData.lateDeductionAfterCount,
                    lateDeductionType: formData.lateDeductionType,
                    lateDeductionAmount: parseFloat(formData.lateDeductionAmount) || 0,
                    autoLockOnPayDay: formData.autoLockOnPayDay ? 1 : 0,
                }),
            })

            if (!response.ok) {
                toast.error('Something went wrong. Please try again.')
                return
            }

            const respo = await response.json()

            if (respo?.status === 'success') {
                /* Show any warnings first */
                if (Array.isArray(respo.response?.warnings)) {
                    respo.response.warnings.forEach((w) => toast.warning(w.message))
                }
                toast.success('Payroll cycle configuration saved.')
                navigate('/payroll/setup/cycle-config/view', {
                    state: {
                        refreshCycleConfig: true,
                    },
                })
            } else {
                if (respo?.message === 'tokenExpired') {
                    authCtx.logout()
                    return
                }

                switch (respo?.errorCode) {
                    case 'CONF_001':
                        toast.info('Configuration already exists. Opening it now.')
                        navigate('/payroll/setup/cycle-config/view', {
                            state: {
                                cycleConfigIdentifier: respo.response?.existingCycleConfigIdentifier,
                            },
                        })
                        break

                    case 'VAL_002':
                        if (respo.message?.includes('cycleStartDay')) {
                            setErrors((prev) => ({ ...prev, cycleStartDay: respo.message }))
                        } else if (respo.message?.includes('payDay')) {
                            setErrors((prev) => ({ ...prev, payDay: respo.message }))
                        } else if (respo.message?.includes('gracePeriodMinutes')) {
                            setErrors((prev) => ({ ...prev, gracePeriodMinutes: respo.message }))
                        } else {
                            toast.error(respo.message)
                        }
                        break

                    case 'VAL_001':
                        setErrors((prev) => ({ ...prev, lateDeductionAmount: respo.message }))
                        break

                    case 'VAL_003':
                        toast.error(respo.message)
                        break

                    default:
                        toast.error(respo?.message || 'Failed to save configuration.')
                        break
                }
            }
        } catch (err) {
            console.error('createStoreSalaryCycleConfig error:', err)
            toast.error('Network error. Please check your connection.')
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className={styles.page}>
            {/* ── Header ── */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)} aria-label="Go back">
                    <ChevronLeft size={18} />
                </button>
                <div className={styles.headerMeta}>
                    <span className={styles.heroPill}>
                        <CalendarClock size={12} />
                        Payroll Setup
                    </span>
                    <h1 className={styles.headerTitle}>Payroll Cycle Setup</h1>
                    <p className={styles.headerSub}>Configure how payroll is calculated and disbursed</p>
                </div>
            </div>

            <div className={styles.formWrap}>

                {/* ── Section: Cycle Frequency ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <CalendarClock size={15} />
                        Cycle Frequency
                    </div>

                    {/* Cycle Type */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            Cycle Type <span className={styles.required}>*</span>
                        </label>
                        <div className={styles.segmentRow}>
                            {[
                                { value: 'MONTHLY', label: 'Monthly' },
                                { value: 'BIWEEKLY', label: 'Bi-weekly' },
                                { value: 'WEEKLY', label: 'Weekly' },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${styles.segmentBtn} ${formData.cycleType === opt.value ? styles.segmentBtnActive : ''}`}
                                    onClick={() => setField('cycleType', opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        <p className={styles.fieldHint}>Most salons use Monthly</p>
                    </div>

                    {/* Cycle Start Day */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            Cycle Start Day <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={28}
                            className={`${styles.textInput} ${errors.cycleStartDay ? styles.inputError : ''}`}
                            value={formData.cycleStartDay}
                            onChange={(e) => setField('cycleStartDay', parseInt(e.target.value, 10) || 1)}
                        />
                        <p className={styles.fieldHint}>Day of month when attendance period begins (1–28)</p>
                        {errors.cycleStartDay && (
                            <p className={styles.fieldError}>{errors.cycleStartDay}</p>
                        )}
                    </div>

                    {/* Pay Day */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>
                            Pay Day <span className={styles.required}>*</span>
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={28}
                            className={`${styles.textInput} ${errors.payDay ? styles.inputError : ''}`}
                            value={formData.payDay}
                            onChange={(e) => setField('payDay', parseInt(e.target.value, 10) || 1)}
                        />
                        <p className={styles.fieldHint}>Day salaries are disbursed (1–28)</p>
                        {errors.payDay && <p className={styles.fieldError}>{errors.payDay}</p>}
                    </div>
                </div>

                {/* ── Section: Pay Date ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <ReceiptText size={15} />
                        Pay Date
                    </div>

                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Pay Day Offset</label>
                        <div className={styles.offsetRow}>
                            {[
                                {
                                    value: 'NEXT_MONTH',
                                    label: 'Next Month',
                                    hint: 'April cycle → paid in May (recommended)',
                                    tag: 'Recommended',
                                },
                                {
                                    value: 'SAME_MONTH',
                                    label: 'Same Month',
                                    hint: 'April cycle → paid in April',
                                    tag: null,
                                },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${styles.offsetCard} ${formData.payDayOffset === opt.value ? styles.offsetCardActive : ''}`}
                                    onClick={() => setField('payDayOffset', opt.value)}
                                >
                                    <span className={styles.offsetLabel}>
                                        {opt.label}
                                        {opt.tag && <span className={styles.offsetTag}>{opt.tag}</span>}
                                    </span>
                                    <span className={styles.offsetHint}>{opt.hint}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Section: LWP Calculation ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Settings2 size={15} />
                        LWP Calculation
                    </div>

                    {/* LWP Method */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>LWP Method</label>
                        <div className={styles.offsetRow}>
                            {[
                                { value: 'PER_WORKING_DAY', label: 'Per Working Day', hint: 'Gross ÷ working days in month' },
                                { value: 'PER_CALENDAR_DAY', label: 'Per Calendar Day', hint: 'Gross ÷ calendar days in month' },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${styles.offsetCard} ${formData.lwpCalculationMethod === opt.value ? styles.offsetCardActive : ''}`}
                                    onClick={() => setField('lwpCalculationMethod', opt.value)}
                                >
                                    <span className={styles.offsetLabel}>{opt.label}</span>
                                    <span className={styles.offsetHint}>{opt.hint}</span>
                                </button>
                            ))}
                        </div>
                        <LwpPreview method={formData.lwpCalculationMethod} />
                    </div>

                    {/* Half Day Counts As */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Half Day Counts As</label>
                        <div className={styles.segmentRow}>
                            {[
                                { value: 0.5, label: '0.5 days (standard)' },
                                { value: 1.0, label: 'Full day (lenient)' },
                            ].map((opt) => (
                                <button
                                    key={opt.value}
                                    type="button"
                                    className={`${styles.segmentBtn} ${formData.halfDayCountsAs === opt.value ? styles.segmentBtnActive : ''}`}
                                    onClick={() => setField('halfDayCountsAs', opt.value)}
                                >
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ── Section: Late Arrival ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        <Clock3 size={15} />
                        Late Arrival
                    </div>

                    {/* Grace Period */}
                    <div className={styles.fieldGroup}>
                        <label className={styles.fieldLabel}>Grace Period (minutes)</label>
                        <input
                            type="number"
                            min={0}
                            max={60}
                            className={`${styles.textInput} ${errors.gracePeriodMinutes ? styles.inputError : ''}`}
                            value={formData.gracePeriodMinutes}
                            onChange={(e) => setField('gracePeriodMinutes', parseInt(e.target.value, 10) || 0)}
                        />
                        <p className={styles.fieldHint}>Minutes after shift start before marking late (0–60)</p>
                        {errors.gracePeriodMinutes && (
                            <p className={styles.fieldError}>{errors.gracePeriodMinutes}</p>
                        )}
                    </div>

                    {/* Late Deduction Toggle */}
                    <div className={styles.toggleRow}>
                        <div className={styles.toggleMeta}>
                            <span className={styles.toggleLabel}>Late Deduction</span>
                            <span className={styles.toggleSub}>
                                Deduct salary for repeated late arrivals beyond the free allowance
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={() => setField('lateDeductionEnabled', !formData.lateDeductionEnabled)}
                            aria-label="Toggle late deduction"
                        >
                            {formData.lateDeductionEnabled ? (
                                <ToggleRight size={28} className={styles.toggleOn} />
                            ) : (
                                <ToggleLeft size={28} className={styles.toggleOff} />
                            )}
                        </button>
                    </div>

                    {/* Late Deduction Detail — shown when enabled */}
                    {formData.lateDeductionEnabled && (
                        <div className={styles.lateDeductionBlock}>
                            {/* Free Lates Allowed */}
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Free Lates Allowed</label>
                                <input
                                    type="number"
                                    min={0}
                                    max={31}
                                    className={styles.textInput}
                                    value={formData.lateDeductionAfterCount}
                                    onChange={(e) =>
                                        setField('lateDeductionAfterCount', parseInt(e.target.value, 10) || 0)
                                    }
                                />
                                <p className={styles.fieldHint}>
                                    Number of lates allowed before deduction starts
                                </p>
                            </div>

                            {/* Deduction Method */}
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Deduction Method</label>
                                <div className={styles.offsetRow}>
                                    {[
                                        {
                                            value: 'PER_LATE',
                                            label: 'Fixed amount per late',
                                            hint: 'Deduct ₹X for each late beyond the free allowance',
                                        },
                                        {
                                            value: 'HALF_DAY_PER_LATE',
                                            label: 'Half-day per late',
                                            hint: 'Each late counts as 0.5 LWP — auto-computed from daily rate',
                                        },
                                    ].map((opt) => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            className={`${styles.offsetCard} ${formData.lateDeductionType === opt.value ? styles.offsetCardActive : ''}`}
                                            onClick={() => setField('lateDeductionType', opt.value)}
                                        >
                                            <span className={styles.offsetLabel}>{opt.label}</span>
                                            <span className={styles.offsetHint}>{opt.hint}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* Amount — only for PER_LATE */}
                            {formData.lateDeductionType === 'PER_LATE' && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>
                                        Amount Per Late (₹){' '}
                                        <span className={styles.required}>*</span>
                                    </label>
                                    <input
                                        type="number"
                                        min={0}
                                        className={`${styles.textInput} ${errors.lateDeductionAmount ? styles.inputError : ''}`}
                                        value={formData.lateDeductionAmount}
                                        placeholder="e.g. 100"
                                        onChange={(e) => setField('lateDeductionAmount', e.target.value)}
                                    />
                                    {errors.lateDeductionAmount && (
                                        <p className={styles.fieldError}>{errors.lateDeductionAmount}</p>
                                    )}
                                </div>
                            )}

                            {/* Info pill for HALF_DAY_PER_LATE */}
                            {formData.lateDeductionType === 'HALF_DAY_PER_LATE' && (
                                <div className={styles.infoCard}>
                                    <Info size={13} />
                                    <p>
                                        Deduction is computed automatically from each staff member's
                                        effective daily rate. No fixed amount needed.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Section: Finalization ── */}
                <div className={styles.sectionCard}>
                    <div className={styles.sectionHead}>
                        {formData.autoLockOnPayDay ? <Lock size={15} /> : <Unlock size={15} />}
                        Finalization
                    </div>

                    <div className={styles.toggleRow}>
                        <div className={styles.toggleMeta}>
                            <span className={styles.toggleLabel}>Auto-Lock on Pay Day</span>
                            <span className={styles.toggleSub}>
                                {formData.autoLockOnPayDay
                                    ? 'Payroll will be finalized automatically on the pay day.'
                                    : 'Manual review required before finalizing. Recommended for most salons.'}
                            </span>
                        </div>
                        <button
                            type="button"
                            className={styles.toggleBtn}
                            onClick={() => setField('autoLockOnPayDay', !formData.autoLockOnPayDay)}
                            aria-label="Toggle auto-lock"
                        >
                            {formData.autoLockOnPayDay ? (
                                <ToggleRight size={28} className={styles.toggleOn} />
                            ) : (
                                <ToggleLeft size={28} className={styles.toggleOff} />
                            )}
                        </button>
                    </div>

                    {!formData.autoLockOnPayDay && (
                        <div className={styles.infoCard}>
                            <Info size={13} />
                            <p>
                                Keeping auto-lock <strong>OFF</strong> gives you time to review
                                attendance and adjustments before salaries are disbursed.
                            </p>
                        </div>
                    )}
                </div>

                {/* ── Config summary preview ── */}
                <div className={styles.summaryCard}>
                    <div className={styles.summaryHead}>
                        <Sparkles size={13} />
                        Configuration Summary
                    </div>
                    <div className={styles.summaryGrid}>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Cycle</span>
                            <span className={styles.summaryVal}>
                                {formData.cycleType === 'MONTHLY'
                                    ? 'Monthly'
                                    : formData.cycleType === 'BIWEEKLY'
                                    ? 'Bi-weekly'
                                    : 'Weekly'}
                                , starts on {formData.cycleStartDay}
                                {formData.cycleStartDay === 1
                                    ? 'st'
                                    : formData.cycleStartDay === 2
                                    ? 'nd'
                                    : formData.cycleStartDay === 3
                                    ? 'rd'
                                    : 'th'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Pay Day</span>
                            <span className={styles.summaryVal}>
                                {formData.payDay}
                                {formData.payDay === 1
                                    ? 'st'
                                    : formData.payDay === 2
                                    ? 'nd'
                                    : formData.payDay === 3
                                    ? 'rd'
                                    : 'th'}{' '}
                                of{' '}
                                {formData.payDayOffset === 'NEXT_MONTH'
                                    ? 'following month'
                                    : 'same month'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>LWP Method</span>
                            <span className={styles.summaryVal}>
                                {formData.lwpCalculationMethod === 'PER_WORKING_DAY'
                                    ? 'Per working day'
                                    : 'Per calendar day'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Half Day</span>
                            <span className={styles.summaryVal}>
                                {formData.halfDayCountsAs === 0.5 ? '0.5 day (standard)' : '1 full day (lenient)'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Grace Period</span>
                            <span className={styles.summaryVal}>{formData.gracePeriodMinutes} min</span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Late Deduction</span>
                            <span className={styles.summaryVal}>
                                {formData.lateDeductionEnabled
                                    ? formData.lateDeductionType === 'HALF_DAY_PER_LATE'
                                        ? `½-day/late after ${formData.lateDeductionAfterCount} free`
                                        : `₹${formData.lateDeductionAmount || 0}/late after ${formData.lateDeductionAfterCount} free`
                                    : 'Disabled'}
                            </span>
                        </div>
                        <div className={styles.summaryItem}>
                            <span className={styles.summaryKey}>Auto-Lock</span>
                            <span className={styles.summaryVal}>
                                {formData.autoLockOnPayDay ? 'Enabled' : 'Manual (recommended)'}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Save Button ── */}
                <button
                    className={`${styles.saveBtn} ${isSaving ? styles.saveBtnDisabled : ''}`}
                    disabled={isSaving}
                    onClick={createCycleConfig}
                >
                    {isSaving ? (
                        <>
                            <LoaderCircle size={16} className={styles.spin} />
                            Saving...
                        </>
                    ) : (
                        <>
                            Save Configuration
                            <ArrowRight size={16} />
                        </>
                    )}
                </button>

            </div>
        </div>
    )
}

export default CycleConfigSetupScreen